import { create } from 'zustand';

import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';

export type ControlCenterTab = 'assistant' | 'notifications';

/** Whether a tab's list is expanded over the chat. Absent until the user has expanded or collapsed it. */
export type ListExpansion = Partial<Record<ControlCenterTab, boolean>>;

type SavedLayout = { tab: ControlCenterTab; expanded: ListExpansion };

type OpenOptions = {
  tab: ControlCenterTab;
  /** For an entry point named after the list, so it opens onto the list rather than the chat. */
  expand?: boolean;
};

type OnboardingDialogState = {
  isOpen: boolean;
  // Bumped on each open so the conversation remounts and re-fetches.
  sessionKey: number;
  /** Unsent composer text. Held here because closing the panel unmounts the composer. */
  draft: string;
  /** Whose layout `tab` and `expanded` are, so one browser shared between accounts keeps them apart. */
  userId: string | null;
  tab: ControlCenterTab;
  expanded: ListExpansion;
  hydrate: (userId: string) => void;
  open: (options?: OpenOptions) => void;
  close: () => void;
  setDraft: (draft: string) => void;
  selectTab: (tab: ControlCenterTab) => void;
  setExpanded: (tab: ControlCenterTab, expanded: boolean) => void;
};

const readLayouts = (): Record<string, SavedLayout> => {
  const stored = localStorageService.get(StorageKeys.CONTROL_CENTER_LAYOUT);
  return stored && typeof stored === 'object' ? stored : {};
};

// The checklist's open state used to be stored on its own, for whoever was signed in. It carries over
// to the first user to load the panel, which on all but a shared browser is the one who set it.
const takeLegacyChecklistExpansion = (): ListExpansion => {
  const legacy = localStorageService.get(StorageKeys.ONBOARDING_CHECKLIST_OPEN);
  localStorageService.remove(StorageKeys.ONBOARDING_CHECKLIST_OPEN);
  return typeof legacy === 'boolean' ? { assistant: legacy } : {};
};

/**
 * The control center panel: whether it is open, and where the user left it.
 *
 * The tab and each tab's expansion are remembered across sessions, so reopening the panel — even
 * after a reload — comes back to what the user was last looking at.
 */
export const useOnboardingDialogStore = create<OnboardingDialogState>((set, get) => ({
  isOpen: false,
  sessionKey: 0,
  draft: '',
  userId: null,
  tab: 'assistant',
  expanded: {},
  hydrate: (userId) => {
    if (get().userId === userId) {
      return;
    }
    const saved = readLayouts()[userId];
    set({
      userId,
      tab: saved?.tab ?? 'assistant',
      expanded: saved?.expanded ?? takeLegacyChecklistExpansion(),
    });
  },
  open: (options) =>
    set((state) => ({
      isOpen: true,
      sessionKey: state.sessionKey + 1,
      ...(options && {
        tab: options.tab,
        expanded: options.expand ? { ...state.expanded, [options.tab]: true } : state.expanded,
      }),
    })),
  close: () => set({ isOpen: false }),
  setDraft: (draft) => set({ draft }),
  selectTab: (tab) => set({ tab }),
  setExpanded: (tab, expanded) => set((state) => ({ expanded: { ...state.expanded, [tab]: expanded } })),
}));

useOnboardingDialogStore.subscribe((state, previous) => {
  if (!state.userId || (state.tab === previous.tab && state.expanded === previous.expanded)) {
    return;
  }
  localStorageService.set(StorageKeys.CONTROL_CENTER_LAYOUT, {
    ...readLayouts(),
    [state.userId]: { tab: state.tab, expanded: state.expanded },
  });
});
